import { useState } from 'react';
import { Check } from 'lucide-react';
import { Button, Input, SectionLabel, sectionLabelVariants } from '@perch/ui';
import { SUPABASE_FUNCTIONS_URL } from '../../lib/supabase';
import { useScrollReveal } from '../../hooks/useScrollReveal';

export function WaitlistSection() {
  const [email, setEmail] = useState('');
  const [interests, setInterests] = useState<string[]>([]);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const section = useScrollReveal();

  const toggleInterest = (interest: string) => {
    setInterests(prev =>
      prev.includes(interest)
        ? prev.filter(i => i !== interest)
        : [...prev, interest]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || interests.length === 0) return;

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${SUPABASE_FUNCTIONS_URL}/waitlist-signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          interests,
          honeypot,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to join waitlist');
      }

      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="waitlist" className="relative py-32 px-4 sm:px-6 bg-white dark:bg-stone-900 scroll-mt-20">
      <div
        ref={section.ref}
        className={`max-w-2xl mx-auto transition-[opacity,transform] duration-500 ease-out ${section.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
      >
        <div className="text-center mb-12">
          <SectionLabel className="mb-3">Waitlist</SectionLabel>
          <h2 className="text-3xl sm:text-4xl font-serif text-stone-900 dark:text-stone-100 mb-4">
            Get on the List
          </h2>
          <p className="text-lg text-stone-500 dark:text-stone-400">
            Tell us what you want to track. We'll let you know when it's your turn.
          </p>
        </div>

        {submitted ? (
          <div className="relative overflow-hidden text-center p-8 bg-emerald-50 dark:bg-emerald-900/20 rounded-2xl border border-emerald-200/60 dark:border-emerald-800/50 animate-[scale-in_300ms_ease-out]">
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 via-transparent to-emerald-500/5 pointer-events-none" />
            <div className="relative">
              <div className="w-16 h-16 bg-gradient-to-br from-emerald-100 to-emerald-50 dark:from-emerald-900/40 dark:to-emerald-900/20 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-emerald-200/50 dark:border-emerald-700/30">
                <Check className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
              </div>
              <h3 className="text-xl font-serif text-stone-900 dark:text-stone-100 mb-2">You're on the List</h3>
              <p className="text-stone-600 dark:text-stone-400">We'll be in touch when {interests.map(i => i.charAt(0).toUpperCase() + i.slice(1)).join(' and ')} tracking is ready for you.</p>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Honeypot field - hidden from humans, bots will fill it */}
            <div className="absolute -left-[9999px]" aria-hidden="true">
              <label htmlFor="website">Website</label>
              <input
                type="text"
                id="website"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
              />
            </div>

            {/* Interest selection */}
            <div>
              <label className={sectionLabelVariants({ variant: 'default' }) + ' block mb-3'}>
                What Do You Want to Track?
              </label>
              <div className="flex flex-wrap gap-3">
                {['Flights', 'Hotels', 'Car Rentals'].map((interest) => (
                  <button
                    key={interest}
                    type="button"
                    onClick={() => toggleInterest(interest.toLowerCase())}
                    className={`px-5 py-2.5 rounded-full text-sm font-medium transition-[background-color,color,box-shadow,border-color] duration-200 ${
                      interests.includes(interest.toLowerCase())
                        ? 'bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900 shadow-lg shadow-stone-900/10'
                        : 'bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-400 hover:bg-stone-200 dark:hover:bg-stone-700 border border-stone-200/60 dark:border-stone-700/50'
                    }`}
                  >
                    {interest}
                  </button>
                ))}
              </div>
            </div>

            {/* Email input */}
            <Input
              id="waitlist-email"
              label="Email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
            />

            {/* Error message */}
            {error && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200/60 dark:border-red-800/50 rounded-xl text-sm text-red-600 dark:text-red-400 animate-[fade-in_200ms_ease-out]">
                {error}
              </div>
            )}

            {/* Submit */}
            <Button
              type="submit"
              disabled={loading || !email || interests.length === 0}
              size="lg"
              fullWidth
              className="flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Joining...
                </>
              ) : (
                'Join Waitlist'
              )}
            </Button>

            <p className="text-xs text-stone-400 dark:text-stone-500 text-center">
              No spam, just updates when we launch new features.
            </p>
          </form>
        )}
      </div>
    </section>
  );
}
